import type { components } from 'admination-react/api-schema'
import { ApiError, fillPath, getJson, postForBlob, sendJson } from '@/core/api/http'

type Schemas = components['schemas']

export type AufnahmePotenzialanalyseQuelle =
  | 'zuweisungsliste'
  | 'teilnahme'
  | 'verbis'
  | 'teilnehmerakte'
  | 'berechnet'
  | 'verbis_ohne_ergebnis'

export type AufnahmePotenzialanalyse = {
  schale: unknown
  werte: Record<string, unknown>
  quellen: Record<string, AufnahmePotenzialanalyseQuelle>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

function isShellShape(value: unknown): value is Record<string, unknown> & { abschnitte: unknown[] } {
  return isRecord(value) && Array.isArray(value.abschnitte)
}

export function isAufnahmePotenzialanalyse(body: unknown): body is AufnahmePotenzialanalyse {
  if (!isRecord(body)) return false
  const { schale, werte, quellen } = body
  return isShellShape(schale) && isRecord(werte) && isRecord(quellen)
}

const MALFORMED_RESPONSE = 'Die Antwort des Servers hatte nicht die erwartete Form.'

export async function getAufnahmePotenzialanalyse(coachingId: string): Promise<AufnahmePotenzialanalyse> {
  const body = await getJson<unknown>(
    fillPath('/aufnahme-potenzialanalyse/{coaching_id}', { coaching_id: coachingId }),
  )
  if (!isAufnahmePotenzialanalyse(body)) throw new Error(MALFORMED_RESPONSE)
  return body
}

export async function fetchAufnahmePotenzialanalyseVorschau(
  coachingId: string,
  werte: Record<string, unknown>,
): Promise<Blob> {
  const path = fillPath('/aufnahme-potenzialanalyse/{coaching_id}/vorschau', { coaching_id: coachingId })
  return postForBlob(path, { werte }, 'application/pdf')
}

export type AufnahmePotenzialanalyseGespeichert = Schemas['AufnahmeSpeichernResult']

function isAufnahmePotenzialanalyseGespeichert(body: unknown): body is AufnahmePotenzialanalyseGespeichert {
  return (
    isRecord(body) &&
    typeof body.dokument_id === 'string' &&
    typeof body.status === 'string' &&
    typeof body.aufgabe === 'string'
  )
}

export async function saveAufnahmePotenzialanalyse(
  coachingId: string,
  werte: Record<string, unknown>,
  bestaetigt = false,
): Promise<AufnahmePotenzialanalyseGespeichert> {
  const path = fillPath('/aufnahme-potenzialanalyse/{coaching_id}', { coaching_id: coachingId })
  const body = await sendJson<unknown>(path, 'POST', { werte, bestaetigt })
  if (!isAufnahmePotenzialanalyseGespeichert(body)) throw new Error(MALFORMED_RESPONSE)
  return body
}

export type AufnahmePotenzialanalyseProblem = { feld: string; label: string; grund: string }

function isAufnahmePotenzialanalyseProblem(value: unknown): value is AufnahmePotenzialanalyseProblem {
  return (
    isRecord(value) &&
    typeof value.feld === 'string' &&
    typeof value.label === 'string' &&
    typeof value.grund === 'string'
  )
}

export function aufnahmePotenzialanalyseProbleme(error: unknown): AufnahmePotenzialanalyseProblem[] | null {
  const probleme = body422(error)?.probleme
  if (!Array.isArray(probleme)) return null
  return probleme.every(isAufnahmePotenzialanalyseProblem) ? (probleme as AufnahmePotenzialanalyseProblem[]) : null
}

function body422(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof ApiError) || error.status !== 422 || !isRecord(error.detail)) return null
  return isRecord(error.detail.detail) ? error.detail.detail : null
}

export type AufnahmePotenzialanalyseHinweis = { feld: string; text: string }

function isAufnahmePotenzialanalyseHinweis(value: unknown): value is AufnahmePotenzialanalyseHinweis {
  return isRecord(value) && typeof value.feld === 'string' && typeof value.text === 'string'
}

export function aufnahmePotenzialanalyseHinweise(error: unknown): AufnahmePotenzialanalyseHinweis[] | null {
  const hinweise = body422(error)?.hinweise
  if (!Array.isArray(hinweise) || hinweise.length === 0) return null
  return hinweise.every(isAufnahmePotenzialanalyseHinweis) ? (hinweise as AufnahmePotenzialanalyseHinweis[]) : null
}

export async function speichereEntwurf(coachingId: string, werte: Record<string, unknown>): Promise<void> {
  const path = fillPath('/aufnahme-potenzialanalyse/{coaching_id}/entwurf', { coaching_id: coachingId })
  await sendJson<void>(path, 'PUT', { werte })
}
